import { useEffect, useMemo, useRef, useState } from 'react';

export interface SeriesPoint {
  label: string;
  /** Primary bar value (e.g. new subs, scans). */
  value: number;
  /** Optional secondary bar drawn side-by-side (e.g. cancellations). */
  value2?: number;
  /** Optional line value on a fixed 0–100 right axis (e.g. active premium normalized, or a rate %). */
  line?: number;
}

interface Props {
  data: SeriesPoint[];
  height?: number;
  /** Legends / tooltip labels for the bar series. */
  valueLabel?: string;
  value2Label?: string;
  lineLabel?: string;
  /** When set, the line uses a fixed [0,100] scale (percentages). Otherwise it auto-scales to its own peak. */
  linePercent?: boolean;
  /** Formatter for the line tooltip value. */
  lineFmt?: (n: number) => string;
}

/**
 * Grouped-bar + overlaid-line chart in the same inline-SVG idiom as
 * EarningsChart / CountBars (no chart-lib dependency). Bars share the left
 * axis (counts); the optional line rides a separate right axis so a rate/%
 * curve reads independently of the bar magnitudes.
 */
export function SeriesChart({
  data,
  height = 240,
  valueLabel = 'value',
  value2Label,
  lineLabel,
  linePercent,
  lineFmt,
}: Props) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [w, setW] = useState(800);
  const [hover, setHover] = useState<number | null>(null);

  useEffect(() => {
    if (!wrapRef.current) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setW(Math.max(320, e.contentRect.width));
    });
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);

  const padT = 12;
  const padB = 26;
  const padL = 8;
  const padR = 8;
  const innerW = w - padL - padR;
  const innerH = height - padT - padB;

  const hasLine = data.some((d) => d.line != null);
  const barMax = Math.max(1, ...data.map((d) => Math.max(d.value, d.value2 ?? 0)));
  const lineMax = linePercent ? 100 : Math.max(1, ...data.map((d) => d.line ?? 0));

  const slot = innerW / Math.max(1, data.length);
  const grouped = value2Label != null;
  const barW = Math.min(grouped ? 16 : 34, slot * (grouped ? 0.32 : 0.6));

  const lineXs = data.map((_, i) => padL + slot * i + slot / 2);
  const lineYs = data.map((d) => padT + innerH - (innerH * (d.line ?? 0)) / (lineMax || 1));

  const linePath = useMemo(() => {
    if (!hasLine || lineXs.length < 2) return '';
    let p = `M ${lineXs[0]} ${lineYs[0]}`;
    for (let i = 1; i < lineXs.length; i++) {
      const cx1 = lineXs[i - 1] + (lineXs[i] - lineXs[i - 1]) * 0.4;
      const cx2 = lineXs[i] - (lineXs[i] - lineXs[i - 1]) * 0.4;
      p += ` C ${cx1} ${lineYs[i - 1]}, ${cx2} ${lineYs[i]}, ${lineXs[i]} ${lineYs[i]}`;
    }
    return p;
  }, [hasLine, lineXs, lineYs]);

  return (
    <div ref={wrapRef} style={{ position: 'relative', width: '100%' }}>
      <svg width={w} height={height} style={{ display: 'block' }}>
        {data.map((d, i) => {
          const centerX = padL + slot * i + slot / 2;
          const isHover = hover === i;
          const h1 = (innerH * d.value) / barMax;
          const y1 = padT + innerH - h1;
          if (grouped) {
            const h2 = (innerH * (d.value2 ?? 0)) / barMax;
            const y2 = padT + innerH - h2;
            const gap = 2;
            const x1 = centerX - barW - gap / 2;
            const x2 = centerX + gap / 2;
            return (
              <g key={i} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
                <rect x={centerX - slot / 2} y={padT} width={slot} height={innerH} fill="transparent" />
                <rect x={x1} y={y1} width={barW} height={Math.max(0, h1)} rx={3} fill="var(--brand)" opacity={isHover ? 1 : 0.9} />
                <rect x={x2} y={y2} width={barW} height={Math.max(0, h2)} rx={3} fill="var(--bad)" opacity={isHover ? 0.9 : 0.7} />
                {(i % Math.ceil(data.length / 8 || 1) === 0 || i === data.length - 1) && (
                  <text x={centerX} y={height - 8} fontSize="10" textAnchor="middle" fill="var(--ink-3)" fontFamily="var(--font-mono)">
                    {d.label}
                  </text>
                )}
              </g>
            );
          }
          const x = centerX - barW / 2;
          return (
            <g key={i} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
              <rect x={centerX - slot / 2} y={padT} width={slot} height={innerH} fill="transparent" />
              <rect x={x} y={y1} width={barW} height={Math.max(0, h1)} rx={3} fill="var(--brand-soft)" opacity={isHover ? 1 : 0.85} />
              {(i % Math.ceil(data.length / 8 || 1) === 0 || i === data.length - 1) && (
                <text x={centerX} y={height - 8} fontSize="10" textAnchor="middle" fill="var(--ink-3)" fontFamily="var(--font-mono)">
                  {d.label}
                </text>
              )}
            </g>
          );
        })}
        {linePath && <path d={linePath} fill="none" stroke="var(--brand-2, var(--brand))" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" />}
        {hover != null && hasLine && data[hover]?.line != null && (
          <circle cx={lineXs[hover]} cy={lineYs[hover]} r="4.5" fill="var(--bg-elev)" stroke="var(--brand-2, var(--brand))" strokeWidth="2.25" />
        )}
      </svg>
      {hover != null && data[hover] && (
        <div
          style={{
            position: 'absolute',
            left: Math.min(w - 170, Math.max(0, padL + slot * hover)),
            top: 0,
            background: 'var(--ink)',
            color: 'var(--bg-elev)',
            padding: '8px 10px',
            borderRadius: 8,
            fontSize: 12,
            pointerEvents: 'none',
            boxShadow: 'var(--shadow-elev)',
            whiteSpace: 'nowrap',
          }}
        >
          <div style={{ opacity: 0.6, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            {data[hover].label}
          </div>
          <div className="num" style={{ fontWeight: 600, fontSize: 13 }}>
            {valueLabel}: {data[hover].value.toLocaleString('en-US')}
          </div>
          {value2Label != null && data[hover].value2 != null && (
            <div style={{ fontSize: 12, opacity: 0.85 }}>
              {value2Label}: {data[hover].value2!.toLocaleString('en-US')}
            </div>
          )}
          {hasLine && data[hover].line != null && lineLabel && (
            <div style={{ fontSize: 12, opacity: 0.85 }}>
              {lineLabel}: {lineFmt ? lineFmt(data[hover].line!) : data[hover].line}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
